/*
 * Iframe half of the widget. This is the real application, unchanged in behaviour,
 * now running inside our own cross-origin transparent iframe (see host-shell.ts).
 *
 * It boots the Vue custom element exactly as before and adds a thin bridge that:
 *   - turns `window.CONSTELL` calls forwarded from the host into event-bus events,
 *   - applies reactive attribute changes forwarded from the host element,
 *   - announces readiness to the host.
 *
 * Analytics relaying (frame -> host) lives in helpers/tracking.ts; iframe resizing
 * (frame -> host) is emitted from App.ce.vue, which owns the open/closed state.
 */
import { i18n } from '@/i18n';
import { plugin as formKitPlugIn } from '@formkit/vue';
import { init } from '@sentry/vue';
import { createPinia } from 'pinia';
import { type Component, createApp } from 'vue';
import { safeHtmlDirective } from '../../../shared/helpers/safe-html';
import AppLoader from '@/AppLoader.ce.vue';
import { defineCustomElement } from '@/defineCustomElementWithStyles';
import eventBusPlugin, { eventBus } from '@/plugins/event-bus-plugin';
import { WIDGET_CLOSE, WIDGET_NAVIGATE, WIDGET_OPEN, WIDGET_TOGGLE } from '@/constants/events';
import { type TargetSection } from '@/typings/widget';
import { type BridgeCommandTyping, buildMessage, FRAME_TO_HOST, HOST_TO_FRAME, readBridgeMessage } from './bridge';

const initSentry = (release: string): void => {
    if (!(import.meta.env?.VITE_ENVIRONMENT && import.meta.env.VITE_SENTRY_DSN)) return;

    init({
        app: createApp(AppLoader as Component),
        dsn: String(import.meta.env.VITE_SENTRY_DSN ?? ''),
        release,
        environment: String(import.meta.env.VITE_ENVIRONMENT ?? ''),
        tracesSampleRate: 1.0,
        tracePropagationTargets: ['*'],
        autoSessionTracking: false,
        defaultIntegrations: false,
        beforeSend(event) {
            const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];
            const isConstellApp = frames.some(frame =>
                typeof frame.filename === 'string'
                && frame.filename?.includes('constell.com')
                && frame.filename?.includes('constell-widget'));

            return isConstellApp ? event : null;
        },
    });
};

// Translate a CONSTELL command forwarded from the host into an event-bus event.
const applyCommand = (payload: BridgeCommandTyping): void => {
    switch (payload.command) {
        case 'open':
            eventBus.emit(WIDGET_OPEN);
            break;
        case 'close':
            eventBus.emit(WIDGET_CLOSE);
            break;
        case 'toggle':
            eventBus.emit(WIDGET_TOGGLE);
            break;
        case 'navigate':
            eventBus.emit(WIDGET_NAVIGATE, {
                target: payload.target as TargetSection,
                payload: payload.payload,
            });
            break;
        default:
            break;
    }
};

// Apply a reactive attribute change forwarded from the host element so Vue picks it up.
const applyConfig = (payload: { name: string, value: string }): void => {
    document.querySelector('constell-widget')
        ?.setAttribute(payload.name, payload.value);
};

// Production: receive CONSTELL calls + config forwarded from the host shell.
const registerHostBridge = (): void => {
    window.addEventListener('message', (event: MessageEvent) => {
        // Commands originate from the host document (the customer page), whose origin
        // we cannot know in advance, so trust the tagged envelope + the parent source.
        if (event.source !== window.parent) return;

        const message = readBridgeMessage(event);
        if (!message) return;

        if (message.type === HOST_TO_FRAME.COMMAND) applyCommand(message.payload as BridgeCommandTyping);
        else if (message.type === HOST_TO_FRAME.CONFIG) applyConfig(message.payload as { name: string, value: string });
    });

    // Tell the host we are listening.
    window.parent?.postMessage(buildMessage(FRAME_TO_HOST.READY), '*');
};

// Local development: no iframe/host shell exists, so expose the public API directly
// on the page window (mirrors the pre-iframe behaviour) for manual testing.
const registerLocalApi = (): void => {
    (window as unknown as Record<string, unknown>).CONSTELL = {
        open: () => { applyCommand({ command: 'open' }) },
        close: () => { applyCommand({ command: 'close' }) },
        toggle: () => { applyCommand({ command: 'toggle' }) },
        navigate: (target: string, payload?: Record<string, unknown>) => {
            applyCommand({
                command: 'navigate',
                target,
                payload,
            });
        },
    };
};

/*
 * Boot the Vue widget. `bridged` is true inside the production iframe (talk to the host
 * shell via postMessage) and false on localhost, where the app is mounted straight into
 * the dev page so Vite HMR, Vue Devtools and the normal dev flow work unchanged.
 */
export const bootstrapApp = ({ bridged, release }: { bridged: boolean, release: string }): void => {
    initSentry(release);

    const pinia = createPinia();

    customElements.define('constell-widget', defineCustomElement(AppLoader, {
        plugins: [
            { plugin: eventBusPlugin },
            { plugin: formKitPlugIn },
            { plugin: i18n },
            { plugin: pinia },
        ],
        directives: { 'safe-html': safeHtmlDirective },
    }));

    if (bridged) registerHostBridge();
    else registerLocalApi();
};
